import React, { useState } from 'react';
import SearchBar from '../../../components/SearchBar';
import { useT } from '../../../i18n/LanguageContext';
import { money } from '../formatters';
import { RECIPIENT_COLUMNS, RECIPIENT_FILTERS } from '../recipientDomain';
import Banner from './Banner';
import LineItems from './LineItems';
import StatementCcEditor from './StatementCcEditor';
import { Stat } from '../tabShared';

export function AdvisoryStrip({ items }) {
  const t = useT();
  const [open, setOpen] = useState(null);
  if (!items.length) return null;
  const shown = items.find((item) => item.key === open);

  return (
    <div className="border border-[#b8d9ff] bg-[#e6f1ff] text-[#005fb8] rounded-lg mb-4">
      <div role="status" className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span
          aria-hidden="true"
          className="material-symbols-outlined text-[18px] leading-5 shrink-0"
        >
          error
        </span>
        <span className="text-sm font-medium mr-1">{t('Before sending')}</span>
        {items.map((item) => (
          <button
            key={item.key}
            id={`advisory-tab-${item.key}`}
            onClick={() => setOpen((current) => (current === item.key ? null : item.key))}
            aria-expanded={open === item.key}
            aria-controls={open === item.key ? `advisory-panel-${item.key}` : undefined}
            className={`text-[13px] leading-5 px-2 py-1 rounded border text-left ${
              open === item.key
                ? 'bg-white border-[#0089ff] font-medium'
                : 'bg-white/60 border-[#b8d9ff] hover:bg-white'
            }`}
          >
            {item.label}
            {item.amount && <span className="ml-1.5 opacity-80">{item.amount}</span>}
          </button>
        ))}
      </div>
      {shown && (
        <div
          id={`advisory-panel-${shown.key}`}
          role="region"
          aria-labelledby={`advisory-tab-${shown.key}`}
          className="border-t border-[#b8d9ff] px-4 py-3 text-sm"
        >
          {shown.body}
        </div>
      )}
    </div>
  );
}

function SortableTh({ label, sortKey, sort, onSort, align = 'left', className = '' }) {
  const t = useT();
  const active = sort.key === sortKey;
  const direction = active ? sort.dir : null;
  return (
    <th
      scope="col"
      className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''} ${className}`}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 hover:text-[#0f172a] ${
          align === 'right' ? 'flex-row-reverse' : ''
        } ${active ? 'text-[#0089ff]' : ''}`}
      >
        {t(label)}
        <span aria-hidden="true" className="material-symbols-outlined text-[16px] leading-none">
          {active ? (direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
        </span>
      </button>
    </th>
  );
}

export default function RecipientView(props) {
  const {
    t,
    staleToday,
    data,
    load,
    refreshError,
    advisories,
    rulingFailures,
    search,
    setSearch,
    only,
    setOnly,
    exportCsv,
    exportNotice,
    refreshing,
    rows,
    sort,
    onSort,
    expanded,
    setExpanded,
    pdfBusy,
    pdfError,
    pdfNotice,
    openPdf,
    pauseBusy,
    setPaused,
    rulingNotice,
    confirmPractice,
    saveStatementCc,
    totals,
    totalsLabel,
  } = props;
  return (
    <div>
      {staleToday && (
        <Banner tone="warn">
          {t('These figures were read on')} <strong>{data.asOf}</strong> {t('and it is now')}{' '}
          <strong>{staleToday}</strong>{' '}
          {t(
            'in Eastern. Balances and ageing have moved on since, and a statement cannot be opened until the page catches up.'
          )}{' '}
          <button onClick={load} className="underline font-medium">
            {t('Refresh now')}
          </button>
        </Banner>
      )}
      {refreshError && <Banner tone="warn">{t(refreshError)}</Banner>}
      {data.environment !== 'production' && (
        <Banner tone="warn">
          {t('These numbers did not come from the live QuickBooks company file')}
          {' ('}
          {data.environment
            ? t('environment: {environment}', { environment: data.environment })
            : t('environment unknown')}
          {').'}
        </Banner>
      )}
      <AdvisoryStrip items={advisories} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label={t('Customers with a balance')} value={data.count ?? 0} />
        <Stat label={t('Total owed')} value={money(data.totalOpen)} />
        <Stat
          label={t('Disagree with QuickBooks')}
          value={data.notReconciling?.count ?? 0}
          tone={data.notReconciling?.count > 0 ? 'warn' : undefined}
        />
        <Stat label={t('Aged as of')} value={data.asOf || '--'} hint={data.environment} />
      </div>

      {Object.keys(rulingFailures).length > 0 && (
        <Banner tone="error">
          <ul className="list-disc pl-5">
            {Object.entries(rulingFailures).map(([id, message]) => (
              <li key={id}>{t(message)}</li>
            ))}
          </ul>
        </Banner>
      )}
      {/* Announced rather than only drawn - and polite, so it does not interrupt. */}
      <p role="status" className="sr-only">
        {t(rulingNotice)}
      </p>
      {pdfNotice && <Banner tone="warn">{t(pdfNotice)}</Banner>}
      {pdfError && <Banner tone="error">{t(pdfError)}</Banner>}

      {/* THE LANDING TAB HAD NO WAY TO RE-READ ITSELF.
          It loads once on mount and is never unmounted again - switching tabs hides it rather than
          tearing it down, so nothing short of a full browser reload could bring these figures up to
          date. That is accounts receivable read live from QuickBooks, on the tab this page opens
          on, ageing silently on screen. */}
      <div className="mb-3 flex items-center gap-2">
        <div className="flex-1">
          {/* SearchBar hands onChange the DOM event, not the value - passing the setter directly
              would put a SyntheticEvent in state and throw on the first keystroke. */}
          <SearchBar
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by customer or email address..."
          />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-[#475569] whitespace-nowrap">
          <select
            value={only}
            onChange={(e) => setOnly(e.target.value)}
            aria-label={t('Filter customers')}
            className="h-[46px] border border-[#e2e8f0] rounded-[10px] px-3 text-sm text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20"
          >
            {RECIPIENT_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>
                {t(f.label)}
              </option>
            ))}
          </select>
        </label>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="text-[13px] text-[#0089ff] hover:underline whitespace-nowrap px-2 disabled:opacity-40 disabled:no-underline"
        >
          {t('Export CSV')}
        </button>
        <button
          onClick={load}
          disabled={refreshing}
          className="text-[13px] text-[#0089ff] hover:underline whitespace-nowrap px-2 disabled:opacity-50 disabled:no-underline"
        >
          {refreshing ? t('Refreshing...') : t('Refresh')}
        </button>
      </div>

      {exportNotice && <Banner tone="info">{t(exportNotice)}</Banner>}

      {/* Identified, because Banner also renders role="status" - several of them can be on screen
          at once, and "the first status region" is not a thing worth depending on. */}
      <p id="statements-totals-status" role="status" className="sr-only">
        {t('{label}, {amount} due.', { label: totalsLabel, amount: money(totals?.totalOpen) })}
      </p>

      <div className="bg-white rounded-xl border border-[#e2e8f0] overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">
            {t(
              'Customers with an open balance, aged into buckets, with how overdue the oldest document on each account is and the amount each owes.'
            )}
          </caption>
          <thead>
            <tr className="text-left text-[12px] text-[#64748b] border-b border-[#e2e8f0]">
              <SortableTh label="Customer" sortKey="customerName" sort={sort} onSort={onSort} />
              <SortableTh
                label="Not overdue"
                sortKey="current"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              <SortableTh
                label="1-30 days"
                sortKey="d1_30"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              <SortableTh
                label="31-60 days"
                sortKey="d31_60"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              <SortableTh
                label="61-90 days"
                sortKey="d61_90"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              <SortableTh
                label="90+ days"
                sortKey="d90_plus"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              {/* How overdue the oldest document is. $5.6m in the 90+ bucket does not say whether
                  that is ninety-one days or four hundred, and those are different conversations. */}
              <SortableTh
                label="Oldest"
                sortKey="oldestDaysPastDue"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              <SortableTh
                label="Total due"
                sortKey="totalOpen"
                sort={sort}
                onSort={onSort}
                align="right"
              />
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={RECIPIENT_COLUMNS} className="px-4 py-8 text-center text-[#64748b]">
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {search || only !== 'all'
                    ? t('No customers match that search or filter.')
                    : data.heldBack?.count > 0
                      ? t(
                          'No customers would receive a statement - every one with a balance is held back, for the reasons above.'
                        )
                      : t('No customers would receive a statement.')}
                </td>
              </tr>
            )}
            {rows.map((s) => {
              const open = expanded === s.qbCustomerId;
              // `known` distinguishes "the server said there is nothing overdue" from "the server
              // did not tell us", which are different sentences and must not share one.
              const known = s.oldestDaysPastDue !== undefined;
              const oldest = Number.isFinite(s.oldestDaysPastDue) ? s.oldestDaysPastDue : null;
              return (
                <React.Fragment key={s.qbCustomerId}>
                  <tr
                    className={`border-b border-[#f1f5f9] last:border-0 ${
                      open ? 'bg-[#f8fafc]' : ''
                    }`}
                  >
                    {/* The row's header, so an amount is announced against the account it belongs
                        to rather than against a column heading alone. */}
                    <th scope="row" className="px-4 py-3 font-normal text-left">
                      <button
                        onClick={() => setExpanded(open ? null : s.qbCustomerId)}
                        aria-expanded={open}
                        className="flex items-start gap-2 text-left group"
                      >
                        <span
                          aria-hidden="true"
                          className={`material-symbols-outlined text-[18px] text-[#94a3b8] mt-0.5 transition-transform ${
                            open ? 'rotate-90' : ''
                          }`}
                        >
                          chevron_right
                        </span>
                        <span>
                          <span className="block font-medium text-[#0f172a] group-hover:text-[#0089ff]">
                            {s.customerName}
                            {/* A SPAN, not a button - which is why this one can live inside the
                                expand toggle when the practice badge could not. It rides the line
                                the name is already on, so a paused customer costs no extra row. */}
                            {s.statementsPaused && (
                              <span
                                title={t(
                                  'An admin paused weekly statements for this customer. They stay on this list and are never sent one.'
                                )}
                                className="ml-2 font-normal break-normal whitespace-nowrap px-1.5 py-0.5 rounded bg-[#f1f5f9] text-[#64748b] text-[11px]"
                              >
                                {t('Paused')}
                              </span>
                            )}
                          </span>
                          {/* NO EMAIL is stated, never left blank. A blank cell reads as "nothing
                              worth showing"; this is the field that decides whether a customer is
                              contacted at all. */}
                          {/* eslint-disable-next-line no-nested-ternary */}
                          {!s.billingEmail?.trim() ? (
                            <span className="block text-[12px] text-[#005fb8]">
                              {t('No billing email - cannot be sent')}
                            </span>
                          ) : s.billingEmailUsable === false ? (
                            // The SERVER decided this, with the same predicate the send planner
                            // uses to refuse the customer. A second copy of the rule in the browser
                            // is how a page starts promising what the send path will not honour.
                            <span className="block text-[12px] text-[#005fb8] break-all">
                              {/* A REAL SPACE, not just a margin. Accessible-name computation does not reliably
                  insert a separator between inline elements, so several screen readers ran the two
                  together as "...DavidL@mhwc.testwill be rejected". `break-normal` stops the
                  address's break-all from splitting the badge's own words. */}
                              {s.billingEmail}{' '}
                              <span
                                title={t(
                                  'The mailbox will not accept this address - usually two addresses in one QuickBooks field. Fix it in QuickBooks.'
                                )}
                                className="ml-1.5 break-normal whitespace-nowrap px-1.5 py-0.5 rounded bg-[#e6f1ff] text-[#005fb8] text-[11px]"
                              >
                                {t('will be rejected')}
                              </span>
                            </span>
                          ) : (
                            <span className="block text-[12px] text-[#94a3b8] break-all">
                              {s.billingEmail}
                              {s.statementCcEmails?.length > 0 && (
                                <span className="ml-2 text-[#475569] whitespace-nowrap">
                                  {' '}
                                  {t('+{count} CC', { count: s.statementCcEmails.length })}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      </button>
                      {/* OUTSIDE the expand button, not inside it.
                          A <button> nested in a <button> is invalid HTML, React warns about it, and
                          the browser gives the inner one's click to the outer one too - so
                          confirming a practice also expanded the row. */}
                      {s.isPractice === null && (
                        <span className="block mt-0.5 ml-6">
                          <span
                            title={t(
                              'Nobody has recorded whether this is a real practice. It would still be sent a statement.'
                            )}
                            className="text-[11px] px-1.5 py-0.5 rounded bg-[#fff8db] text-[#8a6100]"
                          >
                            {t('Unreviewed')}
                          </span>
                          {s.clinicId && (
                            <button
                              onClick={() => confirmPractice(s)}
                              // Named for the customer. Thirty-five buttons reading the same three
                              // words is a list a screen-reader user cannot navigate.
                              aria-label={t('Confirm {name} as a practice', {
                                name: s.customerName,
                              })}
                              className="ml-2 text-[11px] text-[#0089ff] hover:underline"
                            >
                              {t('Confirm as a practice')}
                            </button>
                          )}
                        </span>
                      )}
                    </th>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {money(s.aging?.current, s.currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {money(s.aging?.d1_30, s.currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {money(s.aging?.d31_60, s.currency)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {money(s.aging?.d61_90, s.currency)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right tabular-nums ${
                        s.aging?.d90_plus > 0 ? 'text-[#f23e41] font-medium' : ''
                      }`}
                    >
                      {money(s.aging?.d90_plus, s.currency)}
                    </td>
                    {/* ONE DERIVED VALUE, ONE GUARD.
                        The colour used a coercing `> 90` while the text used `typeof`, so a string
                        "412" off untyped JSON rendered a RED cell reading "--". And `Number.isFinite`
                        is the guard the rest of this file uses - money, csvAmount and the totals
                        reducer all reject NaN, which otherwise printed "NaNd".
                        The absent case says nothing rather than the reassuring thing: the two repos
                        deploy separately, and against a build that does not send this field yet the
                        cell would have told the operator nothing was overdue beside a red 90+ figure
                        showing real money. Silence about what we were not told; never the opposite. */}
                    <td
                      className={`px-4 py-3 text-right tabular-nums whitespace-nowrap ${
                        oldest !== null && oldest > 90
                          ? 'text-[#f23e41] font-medium'
                          : 'text-[#475569]'
                      }`}
                      // Days, not a date: "412 days" is the number a collections conversation turns
                      // on, and the date it resolves to is one row-expansion away.
                      title={
                        oldest !== null
                          ? t(
                              'The oldest overdue document on this account is {days} days past due.',
                              {
                                days: oldest,
                              }
                            )
                          : known
                            ? t('Nothing on this account is overdue.')
                            : t('This deployment did not report how overdue this account is.')
                      }
                    >
                      {oldest !== null ? (
                        t('{days}d', { days: oldest })
                      ) : (
                        <>
                          <span aria-hidden="true" className="text-[#94a3b8]">
                            --
                          </span>
                          {/* `--` already means "no value" in every money cell on this row, so the
                              meaning is spelled out for anyone who cannot hover. */}
                          <span className="sr-only">
                            {known ? t('Nothing overdue') : t('Not reported')}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap">
                      {money(s.totalOpen, s.currency)}
                      {s.reconciles === false && (
                        <span
                          title={t('QuickBooks says {balance}. Difference {delta}.', {
                            balance: money(s.qbBalance, s.currency),
                            delta: money(s.delta, s.currency),
                          })}
                          className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-[#e6f1ff] text-[#005fb8] font-normal"
                        >
                          {t('disagrees')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* A FLEX ROW WITH ONE GAP, rather than a margin on the icon.
                          The gap matches the space the icon has on its other side - the two cells'
                          horizontal padding - so the icon sits evenly between the amount and the
                          link instead of crowding the link. */}
                      <div className="flex items-center justify-end gap-24">
                        {/* An icon, beside the row's other action, rather than a third line under
                            the customer. The label carries the meaning for anyone not looking. */}
                        {s.clinicId && (
                          <button
                            onClick={() => setPaused(s, !s.statementsPaused)}
                            disabled={pauseBusy.includes(s.qbCustomerId)}
                            title={
                              s.statementsPaused
                                ? t('Resume weekly statements')
                                : t('Pause weekly statements')
                            }
                            aria-label={t(
                              s.statementsPaused
                                ? 'Resume statements for {name}'
                                : 'Pause statements for {name}',
                              { name: s.customerName }
                            )}
                            className={`material-symbols-outlined text-[19px] leading-none disabled:opacity-40 ${
                              s.statementsPaused
                                ? 'text-[#0089ff] hover:text-[#005fb8]'
                                : 'text-[#94a3b8] hover:text-[#0089ff]'
                            }`}
                          >
                            {s.statementsPaused ? 'play_circle' : 'pause_circle'}
                          </button>
                        )}
                        <button
                          onClick={() => openPdf(s)}
                          disabled={pdfBusy.includes(s.qbCustomerId)}
                          className="text-[#0089ff] hover:underline disabled:opacity-50 whitespace-nowrap"
                        >
                          {pdfBusy.includes(s.qbCustomerId) ? t('Opening...') : t('View PDF')}
                        </button>
                      </div>
                    </td>
                  </tr>
                  {open && (
                    <tr className="bg-[#f8fafc] border-b border-[#f1f5f9]">
                      <td colSpan={RECIPIENT_COLUMNS} className="px-4 pb-4">
                        <LineItems statement={s} />
                        <StatementCcEditor statement={s} onSave={saveStatementCc} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {/* Rendered only when there is something to total. An "all zeroes" footer under an empty
              table is noise, and under a failed narrowing it would read as a real answer. */}
          {totals && rows.length > 0 && (
            <tfoot className="border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
              <tr className="font-semibold text-[#0f172a]">
                <th scope="row" className="px-4 py-3 text-left text-[13px]">
                  {totalsLabel}
                </th>
                <td className="px-4 py-3 text-right tabular-nums">{money(totals.current)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(totals.d1_30)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(totals.d31_60)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{money(totals.d61_90)}</td>
                <td
                  className={`px-4 py-3 text-right tabular-nums ${
                    totals.d90_plus > 0 ? 'text-[#f23e41]' : ''
                  }`}
                >
                  {money(totals.d90_plus)}
                </td>
                {/* Deliberately blank. An age does not total, and a maximum in a row headed
                    "Total" would read as one. */}
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right tabular-nums whitespace-nowrap">
                  {money(totals.totalOpen)}
                </td>
                <td className="px-4 py-3" />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {data.truncated && (
        <p className="text-[12px] text-[#005fb8] mt-2">
          {/* `count` is the size of the WHOLE target set; `statements` is the capped slice. Printing
              count as "the first N shown" told an operator that every customer was on the page, so
              searching for one past the cap and finding nothing read as "they get no statement". */}
          {t(
            'Showing {shown} of {count} customers - the list was capped, and the search box and filter only narrow what was fetched. A customer missing from this page may still have a balance, and the totals row states which of the two populations it is adding up.',
            { shown: rows.length, count: data.count }
          )}
        </p>
      )}
    </div>
  );
}
